import React, { useEffect, useState, useRef } from "react";
import { AutoComplete, DatePicker } from "../../components/_index";
import {
  Box,
  Button,
  Typography,
  Divider,
  Checkbox,
  Stack,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, Field } from "formik";
import Grid from "@mui/material/Grid2";
import { getQuestion } from "../../services/question.service";
import { postForm, updateForm } from "../../services/form.service";
import ListIcon from "@mui/icons-material/List";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { showToast } from "../../redux/Reducer/toastReducer";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

const AddForm = () => {
  const location = useLocation();
  const selectedData = location.state?.selectedData;
  const formType = location.state?.formType;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [questionData, setQuestionData] = useState([]);
  const [selectedQuestionData, setSelectedData] = useState({
    question: selectedData?.questionInfo.map((question) => question._id) || [],
  });

  // Revert to 'checked' state for the checkbox
  const [checked, setChecked] = useState(selectedData?.isRemember || false);

  const handleCheckboxChange = (id) => {
    setSelectedData((prev) => {
      const currentQuestions = prev.question || [];

      const isChecked = currentQuestions.includes(id);

      let updatedQuestions;

      if (isChecked) {
        updatedQuestions = currentQuestions.filter((item) => item !== id);
      } else {
        updatedQuestions = [...currentQuestions, id];
      }

      return {
        ...prev,
        question: updatedQuestions,
      };
    });
  };

  const initialValues = {
    formName: selectedData?.formName || "",
    reminderText: selectedData?.reminderText || "",
  };

  const validationSchema = yup.object().shape({
    formName: yup.string().required("Form Name Required *"),
    reminderText: yup.string().when("checked", { // Make reminderText mandatory when 'checked' is true
      is: true,
      then: yup.string().required("Reminder Text Required *"),
    }),
  });

  const GetQuestionData = async () => {
    try {
      const res = await getQuestion();
      const modifiedData =
        res?.data.map((e) => ({
          ...e,
          optionName: e?.options?.map((option) => option.optionsName),
          score: e?.options?.map((option) => option.score),
        })) || [];
      // setTableCount(res?.totalCount || 0);
      setQuestionData(modifiedData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    GetQuestionData();
  }, []);

  const onSubmit = async (values) => {
    const selectedQuestions = questionData.filter((question) =>
      selectedQuestionData?.question?.includes(question._id)
    );
    const form = {
      formName: values.formName,
      question: selectedQuestions.map((question) => ({
        _id: question._id,
      })),
      reminderText: values.reminderText,
      isRemember: checked, // Add isRemember based on checkbox state
    };
    const selectedDataId = selectedData?._id;
    try {
      const apiMethod =
        formType === "edit" ? updateForm(selectedDataId, form) : postForm(form);
      const res = await apiMethod;
      GetQuestionData();
      dispatch(showToast({ message: res.message, type: "success" }));
      navigate(-1);
    } catch (error) {
      dispatch(showToast({ message: error.data.message, type: "success" }));
    }
  };

  const triggerSubmit = () => {
    // Access Formik's submitForm function and trigger it
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  return (
    <div
      className="formContainer"
      style={{ border: "1px solid #D5D7E8", padding: "16px" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "10px 14px",
          alignItems: "center",
        }}
      >
        <Typography className="formHeader">
          {formType === "edit" ? "Update Form" : "Create New Form"}
        </Typography>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            className="cancelButton"
            startIcon={<CloseIcon />}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            className="addButton"
            startIcon={<SaveIcon />}
            onClick={triggerSubmit}
          >
            {formType === "edit" ? "Update" : "Create"}
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ padding: "10px 14px" }}>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onSubmit(values);
          }}
        >
          {({ touched, errors, values, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Box mt={2} mb={2}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4, xl: 4 }}>
                    <Typography
                      className="inputTitle"
                      sx={{ fontSize: "12px" }}
                    >
                      Form Name
                    </Typography>
                    <Field
                      name="formName"
                      className="InputField"
                      placeholder="Enter Form Name"
                    />
                    <div className="form-error">
                      {touched.formName && errors.formName && errors.formName}
                    </div>
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 6, md: 4, xl: 4 }}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Checkbox
                      checked={checked}
                      onClick={() => setChecked((prevChecked) => !prevChecked)}
                    />
                    <Typography
                      className="inputTitle"
                      sx={{ fontSize: "12px" }}
                    >
                      Add Remark
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Divider />
              {checked && ( // Use 'checked' state for conditional rendering
                <Stack mt={2} mb={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4, xl: 4 }}>
                    <Typography
                      className="inputTitle"
                      sx={{ fontSize: "12px" }}
                    >
                      Reminder Text
                    </Typography>
                    <Field
                      name="reminderText"
                      className="InputField"
                      placeholder="Enter Reminder Text"
                    />
                    <div className="form-error">
                      {touched.reminderText &&
                        errors.reminderText &&
                        errors.reminderText}
                    </div>
                  </Grid>
                </Stack>
              )}
              <Box mt={2} mb={2}>
                <Stack>
                  {questionData?.map((question, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Checkbox
                        // checked={
                        //   selectedData?.includes(question._id) ||
                        //   datas?.selectedData?.question
                        // }
                        checked={selectedQuestionData?.question?.includes(
                          question._id
                        )}
                        onChange={() => handleCheckboxChange(question._id)}
                      />
                      <Typography>{question?.questionName}</Typography>
                      <Tooltip
                        title={
                          <Stack direction="column" spacing={1}>
                            {question.options?.map((option, optionIndex) => (
                              <Typography key={optionIndex}>
                                {option.optionsName} (Score: {option.score})
                              </Typography>
                            ))}
                          </Stack>
                        }
                        placement="bottom"
                        arrow
                      >
                        <ListIcon sx={{ marginLeft: "30px" }} />
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Divider />
            </Form>
          )}
        </Formik>
      </Box>
    </div>
  );
};

export default AddForm;
